/**
 * Quest progress signals (AD-867) — the moments the player must not miss, read off two
 * consecutive quest logs (server state; never a quest id, never a per-quest rule):
 *
 * - `objective` — one objective just met ("Objective complete: Gather Fenroot Bark").
 * - `ready`     — every objective of a quest is met, so it can be handed in
 *                 ("Ready to turn in: … — return to Warden Sel"). It replaces that quest's
 *                 objective signal in the same update: one moment, one line.
 *
 * Generic over every quest verb: the words come from `objectiveText` (playerText.ts), keyed by
 * verb, so a quest added tomorrow signals with no code change. `questSignals.test.ts` iterates
 * `QUEST_VERBS` so a new verb without words, a marker kind or a turn-in path fails CI.
 *
 * The first log (join / resume, `prev === null`) signals nothing — it is the state the player
 * already had, not a moment.
 */
import { isReadyToTurnIn } from '@great-cloud/shared/client';

import { nameOf, objectiveText } from './playerText.js';

export interface QuestLogLike {
  quests: readonly {
    questId: string;
    name: string;
    status: string;
    turnInNpc?: string | null;
    objectives: readonly {
      verb: string;
      target?: string | null;
      item?: string | null;
      done?: boolean;
    }[];
  }[];
}

export type QuestSignal =
  | { kind: 'objective'; quest: string; text: string }
  | { kind: 'ready'; quest: string; npcName: string | null; text: string };

export function questSignals(prev: QuestLogLike | null, next: QuestLogLike): QuestSignal[] {
  if (prev === null) return [];
  const before = new Map(prev.quests.map((q) => [q.questId, q]));
  const out: QuestSignal[] = [];
  for (const q of next.quests) {
    const was = before.get(q.questId);
    // Ready by the same test as the "!" marker (isReadyToTurnIn): complete, or only talks to the
    // turn-in NPC left — so the toast and the marker flip in the same update.
    if (isReadyToTurnIn(q) && !(was && isReadyToTurnIn(was))) {
      // Ready — also when a quest is accepted already met (the items were in the bag).
      const npcName = q.turnInNpc ? nameOf('npcs', q.turnInNpc) : null;
      out.push({
        kind: 'ready',
        quest: q.name,
        npcName,
        text: `Ready to turn in: ${q.name}${npcName ? ` — return to ${npcName}` : ''}`,
      });
      continue;
    }
    if (!was || q.status !== 'active') continue;
    q.objectives.forEach((o, i) => {
      if (o.done && !was.objectives[i]?.done)
        out.push({
          kind: 'objective',
          quest: q.name,
          text: `Objective complete: ${objectiveText(o)}`,
        });
    });
  }
  return out;
}

/** Desk #279: the tracked find-objective in plain words — what to look for, and where. */
export function findHint(label: string): string {
  return `Look for ${label} inside the gold circle on your map`;
}
